import type { TextFacet } from "../../structs.ts";
import { profilePath } from "../router.ts";

export interface TextSegment {
  readonly text: string;
  readonly facets: readonly TextFacet[];
}

function splitsSurrogate(text: string, index: number): boolean {
  if (index <= 0 || index >= text.length) return false;
  const before = text.charCodeAt(index - 1);
  const after = text.charCodeAt(index);
  return before >= 0xd800 && before <= 0xdbff && after >= 0xdc00 && after <= 0xdfff;
}

export function textSegments(text: string, facets: readonly TextFacet[]): readonly TextSegment[] {
  const boundaries = new Set([0, text.length]);
  for (const facet of facets) {
    if (
      !Number.isSafeInteger(facet.start) || !Number.isSafeInteger(facet.end) || facet.start < 0 ||
      facet.end <= facet.start || facet.end > text.length || splitsSurrogate(text, facet.start) ||
      splitsSurrogate(text, facet.end)
    ) {
      return [{ text, facets: [] }];
    }
    boundaries.add(facet.start);
    boundaries.add(facet.end);
  }

  const points = [...boundaries].sort((a, b) => a - b);
  const result: TextSegment[] = [];
  for (let index = 0; index + 1 < points.length; index++) {
    const start = points[index]!;
    const end = points[index + 1]!;
    const active = facets.filter((facet) => facet.start <= start && facet.end >= end);
    if (active.filter(isSemantic).length > 1) return [{ text, facets: [] }];
    result.push({ text: text.slice(start, end), facets: active });
  }
  return result.length > 0 ? result : [{ text, facets: [] }];
}

export function RichText(
  { text, facets = [] }: { text: string; facets?: readonly TextFacet[] },
): Element {
  const body = <span class="rich-text"></span>;
  for (const segment of textSegments(text, facets)) {
    let node: Node = document.createTextNode(segment.text);
    if (segment.facets.some((facet) => facet.$type === "blue.cerulea.app.facet#italic")) {
      node = <em>{node}</em>;
    }
    if (segment.facets.some((facet) => facet.$type === "blue.cerulea.app.facet#bold")) {
      node = <strong>{node}</strong>;
    }

    const semantic = segment.facets.find(isSemantic);
    if (semantic) {
      switch (semantic.$type) {
        case "blue.cerulea.app.facet#mention":
          node = <a href={profilePath(semantic.did)} dataset={{ route: "" }}>{node}</a>;
          break;
        case "blue.cerulea.app.facet#link": {
          const href = safeExternalUrl(semantic.uri);
          if (href) node = <a href={href} target="_blank" rel="noopener noreferrer">{node}</a>;
          break;
        }
        case "blue.cerulea.app.facet#tag":
          node = <span class="tag">{node}</span>;
          break;
      }
    }
    body.append(node);
  }
  return body;
}

export function safeExternalUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function isSemantic(facet: TextFacet): boolean {
  return facet.$type === "blue.cerulea.app.facet#mention" ||
    facet.$type === "blue.cerulea.app.facet#link" ||
    facet.$type === "blue.cerulea.app.facet#tag";
}
